import { useEffect, useState } from "react";
import { ListChecks, ShieldCheck, Users, Menu } from "lucide-react";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Card } from "../../components/shared/card/card.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/index.ts";
import { OrganizationStructureHero, RolesTab } from "../../components/features/organization-structure/index.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import type { PageProps } from "../../types/page.ts";
import { RoleListTab } from "../role-management/role-management-tabs/role-list-tab.tsx";
import { MenuAssignmentTab } from "../role-management/role-management-tabs/menu-assignment-tab.tsx";
import { UsersRoleReportTab } from "../role-management/role-management-tabs/users-role-report-tab.tsx";
import styles from "./role-management.module.css";

const TABS = [
  { id: "role-list", label: "Role List", icon: ListChecks, component: RoleListTab },
  { id: "menu-assignment", label: "Menu Assignment", icon: Menu, component: MenuAssignmentTab },
  { id: "users-role-report", label: "Users Role Report", icon: Users, component: UsersRoleReportTab },
  { id: "roles", label: "Roles", icon: ShieldCheck, component: RolesTab },
] as const;

export default function RoleManagementPage({ title, description, pageMeta }: PageProps) {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].id);
  const { setBreadcrumbs } = useBreadcrumbs();
  const pageTitle = pageMeta?.title || title || "Role Management"; 

  useEffect(() => {
    setBreadcrumbs([{ label: pageTitle }]);
    return () => setBreadcrumbs([]);
  }, [pageTitle, setBreadcrumbs]);

  return (
    <PageShell>
      <OrganizationStructureHero title="Role Management" description={pageMeta?.description || description || "Manage role definitions, menu access, and employee role assignments in one place."} />
      <Card className={styles.tableCard} padding="5xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} variant="card">
          <TabsList className={styles.tabsList}>
            {TABS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger key={id} value={id}>
                <span className={styles.tabLabel}><Icon size={16} aria-hidden />{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map(({ id, component: Component }) => (
            <TabsContent key={id} value={id}>
              {activeTab === id && <Component />}
            </TabsContent>
          ))}
        </Tabs>
      </Card>
    </PageShell>
  );
}
